import React, { useEffect, useMemo, useState } from 'react';
/**
 * TrueFalseSeriesRenderer — True/false toggle for multiple statements.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s66.
 */
import { QuestionRendererProps } from './types';
import { Check, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { hexToRgba, FALLBACK_COLOR } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function TrueFalseSeriesRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  const statements: Array<{
    text: string;
    isTrue: boolean;
  }> = config.statements || [];
  const [answers, setAnswers] = useState<Record<number, boolean>>({});
  useEffect(() => {
    setAnswers({});
  }, [question.id]);
  useEffect(() => {
    onReadyChange?.(Object.keys(answers).length === statements.length);
  }, [answers, statements.length, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(statements.map((_, idx) => answers[idx] ?? false)), [statements, answers]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const handleSelect = (index: number, value: boolean) => {
    setAnswers(prev => {
      // Toggle: clicking the active button deselects it.
      if (prev[index] === value) {
        const next = {
          ...prev
        };
        delete next[index];
        return next;
      }
      return {
        ...prev,
        [index]: value
      };
    });
  };
  if (statements.length === 0) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-11359">
        <p className="text-red-700 font-bold" data-id="element-11360">⚠️ Geen stellingen gevonden</p>
      </div>;
  }
  return <div className="w-full max-w-2xl mx-auto space-y-4" data-id="element-11361">
      {statements.map((stmt, index) => {
      const isAnswered = answers[index] !== undefined;
      const answer = answers[index];
      return <motion.div key={index} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.1
      }} className={`p-4 sm:p-5 rounded-2xl border-2 transition-all ${isAnswered ? 'bg-white shadow-md' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`} style={isAnswered ? {
        borderColor: hexToRgba(color, 0.3)
      } : undefined} data-id="element-11362">
            <p className="text-slate-500 font-bold text-sm mb-1 uppercase tracking-wider" data-id="element-11363">
              Stelling {index + 1} van {statements.length}
            </p>
            <p className="text-slate-800 font-medium text-lg mb-4" data-id="element-11364">
              {stmt.text}
            </p>
            <div className="flex gap-3" data-id="element-11365">
              <button onClick={() => handleSelect(index, true)} className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold transition-all ${answer === true ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/30 scale-[1.02] border-2 border-indigo-600' : 'bg-white text-slate-700 border-2 border-slate-300 hover:border-indigo-300 hover:bg-indigo-50'}`} data-id="element-11366">
                <Check className={`w-5 h-5 ${answer === true ? 'text-white' : 'text-slate-400'}`} data-id="element-11367" />
                Waar
              </button>
              <button onClick={() => handleSelect(index, false)} className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold transition-all ${answer === false ? 'bg-slate-700 text-white shadow-lg shadow-slate-700/30 scale-[1.02] border-2 border-slate-800' : 'bg-white text-slate-700 border-2 border-slate-300 hover:border-slate-400 hover:bg-slate-50'}`} data-id="element-11368">
                <X className={`w-5 h-5 ${answer === false ? 'text-white' : 'text-slate-400'}`} data-id="element-11369" />
                Onwaar
              </button>
            </div>
          </motion.div>;
    })}
    </div>;
}